(function () {
  const GROUP_ATTRS = {
    flavor: 'data-mm-blog-flavor',
    dish: 'data-mm-blog-dish',
    occasion: 'data-mm-blog-occasion',
    cook_time: 'data-mm-blog-cook-time',
  };

  const handleize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  class MmBlog {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-mm-blog-grid]');
      this.emptyEl = root.querySelector('[data-mm-blog-empty]');
      this.paginationEl = root.querySelector('[data-mm-blog-pagination]');
      this.searchInputs = Array.from(root.querySelectorAll('[data-mm-blog-search]'));
      this.filterInputs = Array.from(root.querySelectorAll('[data-mm-blog-filter-input]'));
      this.drawer = root.querySelector('[data-mm-blog-drawer]');
      this.drawerOpenBtn = root.querySelector('[data-mm-blog-filters-open]');
      this.drawerCloseEls = Array.from(root.querySelectorAll('[data-mm-blog-filters-close]'));
      this.filterGroups = Array.from(root.querySelectorAll('[data-mm-blog-filter-group]'));
      this.searchQuery = '';
      this.searchTimer = null;
      this.ajaxinate = null;
      this.drawerCloseTimer = null;
      this.loadMoreLabel =
        this.paginationEl?.querySelector('[data-mm-blog-load-more] .mm-blog__cta-label')?.textContent?.trim() ||
        'Load more';
      this.viewAllLink = root.querySelector('[data-mm-blog-view-all]');

      this.bindEvents();
      this.initAjaxinate();
      this.applyFilters();
    }

    bindEvents() {
      this.searchInputs.forEach((input) => {
        input.addEventListener('input', () => {
          this.syncSearchInputs(input.value);
          clearTimeout(this.searchTimer);
          this.searchTimer = setTimeout(() => this.applyFilters(), 200);
        });
      });

      this.filterInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.syncFilterInputs(input);
          this.updateActiveGroups();
          this.applyFilters();
        });
      });

      this.root.querySelectorAll('[data-mm-blog-filter-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', () => this.toggleAccordion(toggle));
      });

      if (this.drawerOpenBtn && this.drawer) {
        this.drawerOpenBtn.addEventListener('click', () => this.openDrawer());
      }

      this.drawerCloseEls.forEach((el) => {
        el.addEventListener('click', () => this.closeDrawer());
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeDrawer();
      });

      if (this.paginationEl) {
        this.paginationEl.addEventListener(
          'click',
          (event) => {
            const link = event.target.closest('[data-mm-blog-load-more]');
            if (!link) return;
            this.setCtaLoading(link, true);
          },
          true
        );
      }

      if (this.viewAllLink) {
        this.viewAllLink.addEventListener('click', () => {
          this.setCtaLoading(this.viewAllLink, true);
        });
      }
    }

    setCtaLoading(link, isLoading) {
      if (!link) return;

      if (isLoading) {
        link.style.minWidth = `${link.offsetWidth}px`;
      } else {
        link.style.minWidth = '';
      }

      link.classList.toggle('mm-blog__cta--loading', isLoading);
      link.toggleAttribute('aria-busy', isLoading);
    }

    clearLoadMoreLoading() {
      const link = this.paginationEl?.querySelector('[data-mm-blog-load-more]');
      this.setCtaLoading(link, false);
    }

    syncSearchInputs(value) {
      this.searchQuery = value.trim().toLowerCase();
      this.searchInputs.forEach((input) => {
        if (input.value !== value) input.value = value;
      });
    }

    syncFilterInputs(sourceInput) {
      const group = sourceInput.dataset.mmBlogGroup;
      const value = sourceInput.value;
      const checked = sourceInput.checked;

      this.filterInputs.forEach((input) => {
        if (input.dataset.mmBlogGroup === group && input.value === value) {
          input.checked = checked;
        }
      });
    }

    toggleAccordion(toggle) {
      const group = toggle.closest('[data-mm-blog-filter-group]');
      const panel = group?.querySelector('[data-mm-blog-filter-panel]');
      if (!group || !panel) return;

      const isOpen = group.classList.contains('mm-blog__filter-group--open');
      group.classList.toggle('mm-blog__filter-group--open', !isOpen);
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }

    updateActiveGroups() {
      this.filterGroups.forEach((group) => {
        const groupKey = group.dataset.mmBlogGroup;
        const hasChecked = this.filterInputs.some(
          (input) => input.dataset.mmBlogGroup === groupKey && input.checked
        );
        group.classList.toggle('mm-blog__filter-group--active', hasChecked);
      });
    }

    getActiveFilters() {
      const active = {};

      this.filterInputs.forEach((input) => {
        if (!input.checked) return;
        const group = input.dataset.mmBlogGroup;
        if (!active[group]) active[group] = [];
        active[group].push(handleize(input.value));
      });

      return active;
    }

    hasActiveQuery() {
      return this.searchQuery.length > 0 || Object.keys(this.getActiveFilters()).length > 0;
    }

    cardMatchesFilters(card) {
      const active = this.getActiveFilters();
      const groups = Object.keys(active);

      for (let i = 0; i < groups.length; i += 1) {
        const group = groups[i];
        const attr = GROUP_ATTRS[group];
        if (!attr) return false;

        const cardValues = (card.getAttribute(attr) || '')
          .split(',')
          .map((value) => handleize(value))
          .filter(Boolean);

        const selected = active[group];
        const match = selected.some((value) => cardValues.includes(value));
        if (!match) return false;
      }

      return true;
    }

    cardMatchesSearch(card) {
      if (!this.searchQuery) return true;

      const title = card.dataset.mmBlogTitle || '';
      const excerpt = card.dataset.mmBlogExcerpt || '';
      return title.includes(this.searchQuery) || excerpt.includes(this.searchQuery);
    }

    updatePaginationVisibility(visibleCount) {
      if (!this.paginationEl) return;

      const hasNextPage = Boolean(this.paginationEl.querySelector('a'));
      const showPagination = hasNextPage && (!this.hasActiveQuery() || visibleCount > 0);
      this.paginationEl.hidden = !showPagination;
    }

    applyFilters() {
      if (!this.grid) return;

      const cards = Array.from(this.grid.querySelectorAll('[data-mm-blog-card]'));
      let visibleCount = 0;

      cards.forEach((card) => {
        const visible = this.cardMatchesFilters(card) && this.cardMatchesSearch(card);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (this.emptyEl) {
        this.emptyEl.hidden = visibleCount > 0;
      }

      this.updatePaginationVisibility(visibleCount);
    }

    openDrawer() {
      if (!this.drawer || this.drawer.classList.contains('mm-blog__drawer--open')) return;

      clearTimeout(this.drawerCloseTimer);
      this.drawer.hidden = false;
      if (this.drawerOpenBtn) this.drawerOpenBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.drawer.classList.add('mm-blog__drawer--open');
        });
      });
    }

    closeDrawer() {
      if (!this.drawer) return;

      if (!this.drawer.classList.contains('mm-blog__drawer--open')) {
        if (!this.drawer.hidden) {
          this.drawer.hidden = true;
          document.body.style.overflow = '';
        }
        return;
      }

      this.drawer.classList.remove('mm-blog__drawer--open');
      if (this.drawerOpenBtn) this.drawerOpenBtn.setAttribute('aria-expanded', 'false');

      const panel = this.drawer.querySelector('.mm-blog__drawer-panel');
      const finishClose = () => {
        if (!this.drawer || this.drawer.classList.contains('mm-blog__drawer--open')) return;
        this.drawer.hidden = true;
        document.body.style.overflow = '';
      };

      if (!panel) {
        finishClose();
        return;
      }

      const onTransitionEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(this.drawerCloseTimer);
        finishClose();
      };

      panel.addEventListener('transitionend', onTransitionEnd);
      clearTimeout(this.drawerCloseTimer);
      this.drawerCloseTimer = setTimeout(() => {
        panel.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      }, 400);
    }

    initAjaxinate() {
      if (!this.paginationEl?.querySelector('a') || typeof Ajaxinate === 'undefined') return;

      this.ajaxinate = new Ajaxinate({
        container: '#mm-blog-grid',
        pagination: '#mm-blog-pagination',
        method: 'click',
        offset: 0,
        loadingText: ' ',
        callback: () => {
          this.clearLoadMoreLoading();
          this.applyFilters();
        },
      });
    }
  }

  const initMmBlog = () => {
    document.querySelectorAll('[data-mm-blog]').forEach((root) => {
      new MmBlog(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMmBlog);
  } else {
    initMmBlog();
  }
})();
